import { useCallback, useState } from "react";
import { useFocusEffect } from "@medbillai/ui";
import { createParam } from "@medbillai/navigation/params";
import { FilterLoadState } from "../../lib/filtering/types";
import { isLoading, selectFilter } from "../../lib/filtering/utils";
import { useAlerts } from "../../provider/alerts";
import { BillsList } from "./components/BillsList";
import { useFlaggedEobsCount } from "./utils/useFlaggedEobs";
import { useInsuranceLinkStatus, useNormalizedEobs } from "./utils/useNormalizedEobs";
import { filterMap, filterOptionIds } from "./utils/utils";
const {
  useParam
} = createParam<{
  id: string;
}>();
/**
 * Uses a flatlist as the base screen because of headerTitleLarge requiring a
 * scrollview or flat list as its direct children and when a flatlist is inside
 * of a scrollView it breaks the scroll / distance end threshold detection.
 */
export const BillsScreen = () => {
  const [billId] = useParam("id");
  const [filter, setFilter] = useState(filterOptionIds.all);
  const [endReached, setEndReached] = useState(false);
  const {
    showAlert
  } = useAlerts();
  const [filtersLoaded, setFiltersLoaded] = useState<Record<string, FilterLoadState>>({
    [filterOptionIds.all]: FilterLoadState.LOADING,
    [filterOptionIds.denied]: FilterLoadState.NOT_LOADED,
    [filterOptionIds.inNetwork]: FilterLoadState.NOT_LOADED,
    [filterOptionIds.outOfNetwork]: FilterLoadState.NOT_LOADED
  });
  const {
    dataInsuranceLinks,
    refetch: refetchInsuranceLinks
  } = useInsuranceLinkStatus();
  const {
    data,
    refetch,
    networkStatus
  } = useNormalizedEobs(filterMap[filter]);
  const {
    count: flaggedBillsCount,
    refetch: refetchFlaggedCount
  } = useFlaggedEobsCount();
  useFocusEffect(useCallback(() => {
    if (data.length === 0) {
      void refetch();
    }
  }, [data.length, refetch]));
  const doRefresh = useCallback(async () => {
    try {
      setEndReached(false);
      await refetch();
      void refetchFlaggedCount();
      if (data.length === 0 && dataInsuranceLinks.length) {
        await refetchInsuranceLinks();
      }
    } catch (error) {
      console.error("Error refetching issues:", error);
      showAlert({
        type: "error",
        message: "Something went wrong while refreshing your bills. Please try again in a moment."
      });
    }
  }, [data.length, dataInsuranceLinks.length, refetch, refetchFlaggedCount, refetchInsuranceLinks, showAlert]);
  const handleSelectFilter = useCallback((id: string) => {
    selectFilter(id, setFiltersLoaded, setEndReached, setFilter);
  }, []);
  const fetchMoreItems = useCallback(() => {
    setEndReached(true);
    return Promise.resolve();
  }, []);
  return <BillsList bills={data} fetchMore={fetchMoreItems} onTabPress={handleSelectFilter} onRefresh={doRefresh} loading={isLoading(networkStatus, filtersLoaded[filter])} filter={filter} insuranceLinks={dataInsuranceLinks} endReached={endReached} currentBillId={billId} flaggedBillsCount={flaggedBillsCount} data-sentry-element="BillsList" data-sentry-component="BillsScreen" data-sentry-source-file="bills.tsx" />;
};